// Lookup UK postcode-district (outcode) boundary polygons from
// per-area JSON files shipped under
// `packages/admin-app/public/leads/postcode-boundaries/`. Each file
// holds every district in that area:
//
//   { area: "BS",
//     attribution: "...",
//     outcodes: { "BS1": { type, coordinates }, "BS11": {...}, ... } }
//
// Per-area was a deliberate scaling choice: at expanded radius the
// search includes every neighbour outcode, so fetching one area-level
// file (covers ~30 outcodes for ~100KB) beats fetching 30 individual
// files at ~5KB each on both request count and gzip ratio.
//
// Data is © Wikipedia contributors, CC-BY-SA 3.0, originally from
// https://github.com/missinglink/uk-postcode-polygons. Attribution
// renders on the map overlay where the polygon shows up; see
// `public/leads/postcode-boundaries/README.md` for the
// re-fetch / refresh recipe.

import { useEffect, useState } from "react";

type Polygon = number[][][]; // GeoJSON Polygon `coordinates`
type MultiPolygon = number[][][][]; // GeoJSON MultiPolygon `coordinates`

interface AreaGeometry {
  type: "Polygon" | "MultiPolygon";
  coordinates: Polygon | MultiPolygon;
}

interface AreaFile {
  area: string;
  attribution?: string;
  outcodes: Record<string, AreaGeometry>;
}

const BASE_URL = "/leads/postcode-boundaries/";

// Promise cache so concurrent calls for the same area share one
// fetch. Stays for the lifetime of the page - each area file is
// ~50-200KB and we only load a handful per session.
const areaCache = new Map<string, Promise<AreaFile | null>>();

const fetchArea = (area: string): Promise<AreaFile | null> => {
  const existing = areaCache.get(area);
  if (existing) return existing;
  const promise = fetch(`${BASE_URL}${area}.json`)
    .then((r) => (r.ok ? (r.json() as Promise<AreaFile>) : null))
    .catch(() => null);
  areaCache.set(area, promise);
  return promise;
};

const fetchOutcode = async (
  area: string,
  district: string
): Promise<{
  outcode: string;
  geometry: AreaGeometry;
} | null> => {
  const file = await fetchArea(area);
  if (!file) return null;
  const geometry = file.outcodes[district];
  if (!geometry) return null;
  return { outcode: district, geometry };
};

// Split "BS11" -> "BS" / "BS11", "EC1A" -> "EC" / "EC1A". Postcode
// areas are the leading 1-2 letters; everything from the first digit
// onwards belongs to the district.
const splitOutcode = (
  outcode: string
): { area: string; district: string } | null => {
  const upper = outcode.trim().toUpperCase();
  const match = /^([A-Z]{1,2})\d/.exec(upper);
  if (!match) return null;
  return { area: match[1], district: upper };
};

export interface OutcodeBoundary {
  outcode: string;
  geometry: {
    type: "Polygon" | "MultiPolygon";
    coordinates: Polygon | MultiPolygon;
  };
}

export const useOutcodeBoundary = (
  outcode: string | null
): { data: OutcodeBoundary | null; loading: boolean } => {
  const [state, setState] = useState<{
    data: OutcodeBoundary | null;
    loading: boolean;
  }>({ data: null, loading: false });

  useEffect(() => {
    if (!outcode) {
      setState({ data: null, loading: false });
      return;
    }
    const split = splitOutcode(outcode);
    if (!split) {
      setState({ data: null, loading: false });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true });
    fetchOutcode(split.area, split.district).then((file) => {
      if (cancelled) return;
      if (!file) {
        setState({ data: null, loading: false });
        return;
      }
      setState({
        data: { outcode: file.outcode, geometry: file.geometry },
        loading: false,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [outcode]);

  return state;
};

// Same as `useOutcodeBoundary` but for an array of outcodes (the
// expanded search set: seed outcode plus every neighbour within
// radiusKm). Returns the boundaries that resolved successfully -
// outcodes the dataset doesn't have a polygon for are silently
// dropped. Cache is shared with the single-outcode hook so the seed
// fetch isn't repeated.
export const useOutcodeBoundaries = (
  outcodes: string[] | null
): { data: OutcodeBoundary[]; loading: boolean } => {
  const [state, setState] = useState<{
    data: OutcodeBoundary[];
    loading: boolean;
  }>({ data: [], loading: false });
  // Memoise the join so an unchanged array reference doesn't refire
  // the effect on every render.
  const key = outcodes ? outcodes.slice().sort().join(",") : "";

  useEffect(() => {
    if (!outcodes || outcodes.length === 0) {
      setState({ data: [], loading: false });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ data: prev.data, loading: true }));
    Promise.all(
      outcodes.map((oc) => {
        const split = splitOutcode(oc);
        return split ? fetchOutcode(split.area, split.district) : null;
      })
    ).then((files) => {
      if (cancelled) return;
      const data = files
        .filter(
          (f): f is { outcode: string; geometry: AreaGeometry } => !!f
        )
        .map((f) => ({ outcode: f.outcode, geometry: f.geometry }));
      setState({ data, loading: false });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
};
